import {
  TrashIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  ArrowLeftStartOnRectangleIcon,
} from '@heroicons/react/24/solid';
import {
  Handle,
  Position,
  useStore,
  type Node,
  NodeToolbar,
  NodeResizer,
  useReactFlow,
  OnResizeStart,
  type NodeProps,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { useMemo, useState, Fragment, useEffect, useCallback } from 'react';
import { Button } from 'src/components/button';
import { RotateRightIcon } from 'src/components/icons/RotateRightIcon';
import { useUndoRedo } from 'src/context/undo-redo';
import useDetachNodes from 'src/hooks/useDetachNodes';
import { EEvents, RotateNodesEvent } from 'src/types/events';
import { dispatchNodesChanged } from 'src/utils/dispatch-nodes-changed';
import { getNewDuplicateNode } from 'src/utils/node';
import { twMerge } from 'tailwind-merge';

import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from '../../models/node-data';
import { NodeModel } from '../../models/node-model';
import {
  nodeScale,
  convertRotation,
  calculateEdgeAnimated,
} from '../../models/node-utils';
import StandardNodeDialogEdit from './dialog-edit';
import './standard-node.scss';

function StandardNode({
  id,
  data,
  selected,
  type,
  positionAbsoluteX,
  positionAbsoluteY,
  width,
  height,
}: NodeProps<Node<NodeModel, 'StandardNode'>>) {
  const { takeSnapshot } = useUndoRedo();

  const [onHover, setOnHover] = useState(false);
  const { setNodes, getNodes, setEdges } = useReactFlow();
  const nodes = getNodes() as Node<NodeModel>[];

  const isMultiSelected = nodes.filter((node) => node.selected).length > 1;

  const [rotation, setRotation] = useState(data.category.rotation || 0);
  const updateNodeInternals = useUpdateNodeInternals();
  const [isEditing, setIsEditing] = useState(false);

  const handleRotate = useCallback(
    (isSnapshot = true) => {
      if (isSnapshot) {
        takeSnapshot();
      }
      if (rotation === 270) {
        setRotation(0);
      } else {
        setRotation(rotation + 90);
      }
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === id) {
            const newRotation = rotation === 270 ? 0 : rotation + 90;
            const newPosition = {
              x: positionAbsoluteX + (width ?? 0) / 2 - (height ?? 0) / 2,
              y: positionAbsoluteY + (height ?? 0) / 2 - (width ?? 0) / 2,
            };
            return {
              ...node,
              position: newPosition,
              data: {
                ...node.data,
                category: {
                  ...(node.data?.category as NodeCategoryModel),
                  rotation: newRotation,
                },
              },
            };
          }
          return node;
        }),
      );
      updateNodeInternals(id);
    },
    [
      id,
      positionAbsoluteX,
      positionAbsoluteY,
      width,
      height,
      rotation,
      setNodes,
      updateNodeInternals,
      takeSnapshot,
    ],
  );

  const dataCovert = useMemo(
    () => convertRotation(rotation, data),
    [data, rotation],
  );

  const handleSave = (newData: Partial<NodeCategoryModel>) => {
    takeSnapshot();
    setNodes((nds) =>
      nds.map((node: any) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                category: {
                  ...node.data?.category,
                  ...newData,
                },
              },
            }
          : node,
      ),
    );
    dispatchNodesChanged([id]);
    setEdges((eds) => {
      // Update nodes first to get the latest node data
      const updatedNodes = nodes.map((node: any) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                category: {
                  ...node.data?.category,
                  ...newData,
                },
              },
            }
          : node,
      );

      // Calculate animated property correctly for all edges connected to this node
      const newEdges = eds.map((edge) => {
        if (edge.source === id || edge.target === id) {
          const isAnimated = calculateEdgeAnimated(
            edge.source,
            edge.target,
            updatedNodes,
          );
          return {
            ...edge,
            animated: isAnimated,
            data: {
              ...edge.data,
            },
          };
        }
        return edge;
      });
      return newEdges;
    });
  };

  const onDelete = useCallback(() => {
    takeSnapshot();
    setNodes((nds) => nds.filter((node) => node?.id !== id));
    const allAvailableNodeIds = nodes
      .map((node) => node.id)
      .filter((nodeId) => nodeId !== id);

    setEdges((eds) =>
      eds.filter(
        (edge) =>
          edge.source !== id &&
          edge.target !== id &&
          allAvailableNodeIds.includes(edge.source) &&
          allAvailableNodeIds.includes(edge.target),
      ),
    );
    dispatchNodesChanged(allAvailableNodeIds);
  }, [id, setEdges, setNodes, takeSnapshot, nodes]);

  const onDuplicate = useCallback(() => {
    takeSnapshot();
    const targetNode = nodes.find((node) => node.id === id) as Node<NodeModel>;
    const newNode = getNewDuplicateNode(
      targetNode,
      nodes,
      positionAbsoluteX +
        (targetNode.data.category.viewSize.width * nodeScale + 10),
      positionAbsoluteY,
    );
    setNodes((nds) => [...nds, newNode]);
    dispatchNodesChanged([newNode.id]);
  }, [id, nodes, positionAbsoluteX, positionAbsoluteY, setNodes, takeSnapshot]);

  const hasParent = useStore((store) => !!store.nodeLookup.get(id)?.parentId);
  const detachNodes = useDetachNodes();

  const onDetach = () => detachNodes([id]);

  const onDoubleClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (data.category.isValid === undefined) {
      dispatchNodesChanged([id]);
    }
    setRotation(data.category.rotation || 0);
  }, [data.category.isValid, data.category.rotation, id]);

  const isManaged = useMemo(() => {
    if (data.category.type === EDataType.Device) return true;
    return data.category.switchType?.includes(EDataSwitchType.MANAGED);
  }, [data.category.switchType, data.category.type]);

  const handleClick = (event: React.MouseEvent) => {
    if (event.shiftKey && selected) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                selected: !selected,
              }
            : node,
        ),
      );
    }
  };

  const onResizeStart: OnResizeStart = useCallback(
    (event, node) => {
      takeSnapshot();
    },
    [takeSnapshot],
  );

  // listen to rotate nodes event
  useEffect(() => {
    const handleRotateNodes = (event: CustomEvent<RotateNodesEvent>) => {
      if (event.detail.nodeIds.includes(id)) {
        handleRotate(false);
      }
    };
    window.addEventListener(
      EEvents.ROTATE_NODES,
      handleRotateNodes as EventListener,
    );
    return () => {
      window.removeEventListener(
        EEvents.ROTATE_NODES,
        handleRotateNodes as EventListener,
      );
    };
  }, [handleRotate, id]);

  const getImageUrl = useCallback(
    (url: string) => `${(url ?? '').replace('./', '/')}`,
    [],
  );

  return (
    <div
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}
      onClick={handleClick}
      className={twMerge(
        'dnd-node standard-node',
        selected && 'selected',
        onHover && 'hover',
      )}
      onDoubleClick={onDoubleClick}
    >
      <NodeToolbar
        isVisible={onHover && !isMultiSelected}
        position={Position.Top}
      >
        <div
          data-testid={`node-toolbar-info-${id}`}
          className="relative flex origin-top flex-col rounded-md border border-gray-300 bg-gray-100 px-2 pb-2 pt-3 text-left shadow-xl transition duration-200 ease-out"
        >
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold">Name:</span>
            <span className="text-sm">{data.category.name}</span>
          </div>
          {isManaged && (
            <>
              {data.category.ipInterfaces &&
              data.category.ipInterfaces.length > 0 ? (
                <>
                  {data.category.ipInterfaces.map((ipInterface, index) => (
                    <div className="flex items-center gap-1" key={index}>
                      <span className="text-sm font-bold">
                        IP ({ipInterface.vlanId}):
                      </span>
                      <span className="text-sm">{ipInterface.ip}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-sm font-bold">IP:</span>
                  <span className="text-sm">{data.category.ip}</span>
                </div>
              )}
            </>
          )}
        </div>
      </NodeToolbar>
      {!isMultiSelected && (
        <NodeToolbar isVisible={selected} position={Position.Bottom}>
          <div
            data-testid={`node-toolbar-actions-${id}`}
            className="flex w-full items-center justify-center gap-2"
          >
            <Button
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Delete"
              color="red"
              data-testid={`node-delete-button-${id}`}
              onClick={() => onDelete()}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
            {isManaged && (
              <Button
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Edit"
                color="blue"
                data-testid={`node-edit-button-${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <PencilSquareIcon className="h-4 w-4" />
              </Button>
            )}
            <Button
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Duplicate"
              color="yellow"
              data-testid={`node-duplicate-button-${id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDuplicate();
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
            </Button>
            {data.category.rotatable && (
              <Button
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Rotate"
                color="green"
                data-testid={`node-rotate-button-${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRotate();
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <RotateRightIcon className="h-4 w-4" />
              </Button>
            )}
            {hasParent && (
              <Button
                data-tooltip-id="app-tooltip"
                data-tooltip-content="Detach"
                color="orange"
                data-testid={`node-detach-button-${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDetach();
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ArrowLeftStartOnRectangleIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </NodeToolbar>
      )}

      {data.category.resizable && (
        <NodeResizer
          color={data.category.color}
          isVisible={selected}
          minWidth={50}
          minHeight={50}
          maxWidth={200}
          maxHeight={200}
          keepAspectRatio
          onResizeStart={onResizeStart}
        />
      )}
      <div
        style={{
          ...(!data.category.resizable && {
            width: `${dataCovert.width}px`,
            height: `${dataCovert.height}px`,
          }),
        }}
        className="standard-node-body relative h-full w-full"
      >
        <div
          style={{
            background: data.category.color,
          }}
          className="node-category-color relative"
        />
        {dataCovert.image ? (
          <>
            <span
              className={twMerge(
                'absolute bottom-0 left-0 bg-black bg-opacity-50 px-1 text-base leading-tight text-white',
              )}
            >
              {data.category.name}
            </span>
            <img
              className="h-full w-full object-contain"
              src={getImageUrl(dataCovert.image)}
              alt={data.category.name}
            />
          </>
        ) : (
          <span>{data.category.name}</span>
        )}
      </div>
      {dataCovert.ports?.map((port, pIndex) => (
        <Fragment key={pIndex}>
          {port.type &&
            port.type.length > 0 &&
            port.type.map((portType, index) => (
              <Handle
                key={`${pIndex}-${index}`}
                isConnectable
                id={`${id}__${port.id}__${portType}`}
                type={portType}
                className={`standard-node-port port-${port.connectionType}`}
                position={port.position}
                style={port.style}
              />
            ))}
        </Fragment>
      ))}
      {/* {data.category?.ports?.map((port) => (
              <Handle
                isConnectable
                id={`${id}-${port.id}`}
                type={port.type[0]}
                position={port.position}
                style={port.style}
              />
            ))} */}
      <StandardNodeDialogEdit
        id={id}
        labelKey={data.key}
        open={isEditing}
        onClose={() => setIsEditing(false)}
        data={data.category}
        onSaved={handleSave}
      />
      {!data.category.isValid && (
        <div className="standard-node-noti absolute right-1 top-1 z-50">
          <ExclamationTriangleIcon className="node-warning h-3 w-3 text-red-500" />
        </div>
      )}
    </div>
  );
}

export default StandardNode;
